// deactivate-dialog.component.ts
import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { DeletionStrategy } from '../services/super-admin.service';

export interface DeactivateDialogData {
  organizationName: string;
  organizationId: string;
  hasData: boolean;
}

@Component({
  selector: 'app-deactivate-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    FormsModule
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>warning</mat-icon>
      Deactivate Organization
    </h2>
    <mat-dialog-content>
      <p>You are about to deactivate <strong>{{ data.organizationName }}</strong>.</p>
      
      <div class="strategy-selection">
        <h3>Choose deactivation method:</h3>
        <mat-radio-group [(ngModel)]="selectedStrategy" class="strategy-group">
          <mat-radio-button [value]="DeletionStrategy.SoftDelete" class="strategy-option">
            <div class="strategy-info">
              <div class="strategy-title">
                <mat-icon>visibility_off</mat-icon>
                <strong>Deactivate</strong>
                <span class="badge-recommended">Recommended</span>
              </div>
              <p class="strategy-desc">
                Deactivate organization and lock user accounts. All data is retained and can be reactivated later.
              </p>
            </div>
          </mat-radio-button>

          <mat-radio-button [value]="DeletionStrategy.Archive" class="strategy-option">
            <div class="strategy-info">
              <div class="strategy-title">
                <mat-icon>archive</mat-icon>
                <strong>Archive</strong>
              </div>
              <p class="strategy-desc">
                Move to archived section. Organization will be hidden from active lists but can be restored.
              </p>
            </div>
          </mat-radio-button>

          @if(!data.hasData) {
            <mat-radio-button [value]="DeletionStrategy.HardDelete" class="strategy-option">
              <div class="strategy-info">
                <div class="strategy-title">
                  <mat-icon color="warn">delete_forever</mat-icon>
                  <strong>Permanent Delete</strong>
                  <span class="badge-warning">Cannot be undone</span>
                </div>
                <p class="strategy-desc">
                  Permanently delete organization and all associated data. This action is irreversible.
                </p>
              </div>
            </mat-radio-button>
          }
        </mat-radio-group>
      </div>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Reason (Optional)</mat-label>
        <textarea
          matInput
          [(ngModel)]="reason"
          placeholder="Why is this organization being deactivated?"
          rows="3"
        ></textarea>
      </mat-form-field>

      @if(data.hasData && selectedStrategy === DeletionStrategy.HardDelete) {
        <div class="warning-box">
          <mat-icon>error</mat-icon>
          <p>Permanent deletion is not available for organizations with existing data.</p>
        </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button
        mat-flat-button
        [color]="selectedStrategy === DeletionStrategy.HardDelete ? 'warn' : 'primary'"
        (click)="onConfirm()"
        [disabled]="data.hasData && selectedStrategy === DeletionStrategy.HardDelete"
      >
        <mat-icon>{{ getButtonIcon() }}</mat-icon>
        {{ getButtonText() }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content {
      min-width: 500px;
    }

    .strategy-selection {
      margin: 20px 0;
    }

    .strategy-selection h3 {
      margin-bottom: 16px;
      font-size: 1rem;
      font-weight: 500;
    }

    .strategy-group {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .strategy-option {
      padding: 16px;
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      transition: all 0.2s;
    }

    .strategy-option:hover {
      border-color: #1976d2;
      background: #f5f5f5;
    }

    .strategy-option.mat-mdc-radio-button-checked {
      border-color: #1976d2;
      background: #e3f2fd;
    }

    .strategy-info {
      margin-left: 8px;
    }

    .strategy-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .strategy-title mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .strategy-desc {
      margin: 0;
      font-size: 0.875rem;
      color: #666;
      line-height: 1.4;
    }

    .badge-recommended,
    .badge-warning {
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .badge-recommended {
      background: #4caf50;
      color: white;
    }

    .badge-warning {
      background: #ff9800;
      color: white;
    }

    .full-width {
      width: 100%;
      margin-top: 16px;
    }

    .warning-box {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: #fff3e0;
      border-left: 4px solid #ff9800;
      border-radius: 4px;
      margin-top: 16px;
    }

    .warning-box mat-icon {
      color: #ff9800;
    }

    .warning-box p {
      margin: 0;
      font-size: 0.875rem;
    }
  `]
})
export class DeactivateDialogComponent {
  selectedStrategy = DeletionStrategy.SoftDelete;
  reason = '';
  DeletionStrategy = DeletionStrategy;

  constructor(
    public dialogRef: MatDialogRef<DeactivateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeactivateDialogData
  ) {}

  getButtonText(): string {
    switch (this.selectedStrategy) {
      case DeletionStrategy.SoftDelete:
        return 'Deactivate';
      case DeletionStrategy.Archive:
        return 'Archive';
      case DeletionStrategy.HardDelete:
        return 'Permanently Delete';
      default:
        return 'Confirm';
    }
  }

  getButtonIcon(): string {
    switch (this.selectedStrategy) {
      case DeletionStrategy.SoftDelete:
        return 'visibility_off';
      case DeletionStrategy.Archive:
        return 'archive';
      case DeletionStrategy.HardDelete:
        return 'delete_forever';
      default:
        return 'check';
    }
  }

  onConfirm() {
    this.dialogRef.close({
      strategy: this.selectedStrategy,
      reason: this.reason || undefined
    });
  }

  onCancel() {
    this.dialogRef.close();
  }
}